import { createSelector } from "reselect";

import type {
  FiPresetRoleKey,
  FiWeeklySummaryPreset,
  FiWordCloudPreset,
} from "@mapmycustomers/shared/types/entity/fieldInsight";

import { getOrganizationId } from "@app/store/iam/selectors";
import type { RootState } from "@app/store/rootReducer";

const organizationDetailsState = (state: RootState) => state.scene.settings.organizationDetails;

const EMPTY_PRESETS: ReadonlyMap<FiPresetRoleKey, FiWeeklySummaryPreset> = new Map();
const EMPTY_WORD_CLOUD_PRESETS: ReadonlyMap<FiPresetRoleKey, FiWordCloudPreset> = new Map();

export const getAffectedUsers = createSelector(
  organizationDetailsState,
  ({ affectedUsers }) => affectedUsers
);
export const getAffectedUsersTotal = createSelector(
  organizationDetailsState,
  ({ affectedUsersTotal }) => affectedUsersTotal
);
export const areAffectedUsersLoading = createSelector(
  organizationDetailsState,
  ({ affectedUsersLoading }) => affectedUsersLoading
);

// Presets outlive an org switch in the store, so they are only served back to their own org —
// otherwise the previous org's coverage counts could show before the refetch lands.
export const getExecutiveSummaryPresetsByRole = createSelector(
  organizationDetailsState,
  getOrganizationId,
  ({ executiveSummaryPresets, executiveSummaryPresetsOrganizationId }, organizationId) =>
    executiveSummaryPresetsOrganizationId === organizationId
      ? new Map(executiveSummaryPresets.map((preset) => [preset.roleKey, preset]))
      : EMPTY_PRESETS
);
export const areExecutiveSummaryPresetsLoading = createSelector(
  organizationDetailsState,
  ({ executiveSummaryPresetsLoading }) => executiveSummaryPresetsLoading
);
export const didExecutiveSummaryPresetsFail = createSelector(
  organizationDetailsState,
  ({ executiveSummaryPresetsFailed }) => executiveSummaryPresetsFailed
);
export const areExecutiveSummaryPresetsUpdating = createSelector(
  organizationDetailsState,
  ({ executiveSummaryPresetsUpdating }) => executiveSummaryPresetsUpdating
);

export const getWordCloudPresetsByRole = createSelector(
  organizationDetailsState,
  getOrganizationId,
  ({ wordCloudPresets, wordCloudPresetsOrganizationId }, organizationId) =>
    wordCloudPresetsOrganizationId === organizationId
      ? new Map(wordCloudPresets.map((preset) => [preset.roleKey, preset]))
      : EMPTY_WORD_CLOUD_PRESETS
);
export const areWordCloudPresetsLoading = createSelector(
  organizationDetailsState,
  ({ wordCloudPresetsLoading }) => wordCloudPresetsLoading
);
export const didWordCloudPresetsFail = createSelector(
  organizationDetailsState,
  ({ wordCloudPresetsFailed }) => wordCloudPresetsFailed
);
export const areWordCloudPresetsUpdating = createSelector(
  organizationDetailsState,
  ({ wordCloudPresetsUpdating }) => wordCloudPresetsUpdating
);
